/**
 * Condicionais — a listagem da venda condicional (consignação de balcão).
 *
 * Padrão de `TelaClientes.tsx`: busca + chips + tabela + paginação, com uma
 * gaveta de ficha para leitura. Os chips de status usam `Segmentado` no papel
 * `filtro` — a lista continua sendo a MESMA lista recortada, nunca uma troca
 * de aba (ver o comentário de `Segmentado.tsx`).
 *
 * Cada linha ABERTA tem três ações — Faturar, Devolver, Cancelar — e as
 * outras só têm "Ver ficha": faturada, devolvida e cancelada são estados
 * finais (mesmo `check` da migração 0024, sem caminho de volta).
 *
 * "Faturar" navega para `/app/condicionais/:codigo/faturar` — rota própria,
 * e não gaveta. A razão está no comentário de topo de
 * `TelaFaturarCondicional.tsx`: encaixar o `Fechamento` do PDV (que já abre
 * a própria gaveta para dividir pagamento) dentro de OUTRA gaveta empilharia
 * dois modais com foco preso ao mesmo tempo, com dois ouvintes de Esc
 * competindo pelo mesmo evento.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { LinkBotao } from '../../componentes/Botao'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta } from '../../componentes/Gaveta'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import { Segmentado } from '../../componentes/Segmentado'
import { mostrarAviso } from '../../componentes/avisos'
import { ErroApi } from '../../plataforma/api'
import {
  carregarCondicional,
  carregarCondicionais,
  FILTROS_STATUS,
  type Condicional,
  type FiltroStatus,
  type LinhaCondicional,
  type ListaDeCondicionais,
} from '../../plataforma/condicional'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { dataISOparaBR } from '../../plataforma/mascaras'
import { useSessao } from '../../plataforma/contexto-sessao'
import { LayoutApp } from '../app/LayoutApp'
import { DialogoCancelarCondicional } from './DialogoCancelarCondicional'
import { DialogoDevolverCondicional } from './DialogoDevolverCondicional'

const ICONE_STATUS: Record<string, NomeIcone> = {
  aberta: 'atraso',
  faturada: 'check_circle',
  devolvida: 'apparel',
  cancelada: 'close',
}

export function TelaCondicionais() {
  const navegar = useNavigate()
  const { pode } = useSessao()
  const podeCriar = pode('sale.create')

  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [status, setStatus] = useState<FiltroStatus>('todas')
  const [lista, setLista] = useState<ListaDeCondicionais | null>(null)
  const [erro, setErro] = useState('')
  const [versao, setVersao] = useState(0)

  const [aberta, setAberta] = useState<Condicional | null>(null)
  const [devolvendo, setDevolvendo] = useState<LinhaCondicional | null>(null)
  const [cancelando, setCancelando] = useState<LinhaCondicional | null>(null)

  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    const controle = new AbortController()
    carregarCondicionais({ busca: buscaAtiva, status }, controle.signal)
      .then((dados) => {
        setLista(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar as condicionais.')
      })
    return () => controle.abort()
  }, [buscaAtiva, status, versao])

  const abrirFicha = useCallback(async (codigo: string) => {
    try {
      setAberta(await carregarCondicional(codigo))
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir a ficha.')
    }
  }, [])

  const subtitulo = lista
    ? `${lista.total} ${lista.total === 1 ? 'condicional encontrada' : 'condicionais encontradas'}`
    : 'Carregando…'

  return (
    <LayoutApp
      titulo="Condicional"
      subtitulo={subtitulo}
      acoes={
        <LinkBotao para="/app/condicionais/novo" icone="add">
          Nova condicional
        </LinkBotao>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {erro && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto"
          >
            <span className="min-w-0 flex-1">{erro}</span>
            <button
              type="button"
              onClick={() => setVersao((n) => n + 1)}
              className="h-8 shrink-0 rounded-sistema border border-erro bg-superficie px-3 text-[12.5px] font-semibold text-erro"
            >
              Tentar de novo
            </button>
          </div>
        )}

        <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
          <div className="flex flex-wrap items-center gap-2.5 border-b border-borda px-4 py-3">
            <label className="composto h-[34px] min-w-0 flex-1 basis-[240px] cursor-text">
              <Icone nome="search" tamanho={18} className="text-texto-fraco" />
              <input
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                placeholder="Buscar por código ou cliente"
              />
              <span className="sr-only">Buscar condicional</span>
            </label>

            <Segmentado
              opcoes={FILTROS_STATUS}
              valor={status}
              aoTrocar={setStatus}
              rotuloDoGrupo="Filtrar condicionais por status"
            />
          </div>

          {!lista ? (
            <Esqueleto />
          ) : lista.linhas.length === 0 ? (
            <EstadoVazio
              temFiltro={buscaAtiva !== '' || status !== 'todas'}
              podeCriar={podeCriar}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse">
                <caption className="sr-only">Condicionais abertas, faturadas, devolvidas e canceladas</caption>
                <thead>
                  <tr className="bg-fundo">
                    {['Código', 'Cliente', 'Itens', 'Subtotal', 'Prazo', 'Situação', ''].map(
                      (titulo, i) => (
                        <th
                          key={titulo || 'acoes'}
                          scope="col"
                          className={`border-b border-borda py-2.5 text-left text-[12.5px] font-semibold text-texto-suave ${
                            i === 0 ? 'pr-3 pl-4' : 'px-3'
                          }`}
                        >
                          {titulo}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {lista.linhas.map((linha, i) => (
                    <LinhaDaTabela
                      key={linha.codigo}
                      linha={linha}
                      ultima={i === lista.linhas.length - 1}
                      aoAbrir={() => void abrirFicha(linha.codigo)}
                      aoFaturar={() => navegar(`/app/condicionais/${encodeURIComponent(linha.codigo)}/faturar`)}
                      aoDevolver={() => setDevolvendo(linha)}
                      aoCancelar={() => setCancelando(linha)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lista && lista.linhas.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-borda px-4 py-2.5">
              <span className="tabular text-[13px] text-texto-suave">
                {lista.linhas.length} de {lista.total}{' '}
                {lista.total === 1 ? 'condicional' : 'condicionais'}
              </span>
              {lista.paginas > 1 && (
                <span className="text-[12.5px] text-texto-fraco">
                  Página {lista.pagina} de {lista.paginas}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {aberta && <Drawer condicional={aberta} aoFechar={() => setAberta(null)} />}

      {devolvendo && (
        <DialogoDevolverCondicional
          condicional={devolvendo}
          aoFechar={() => setDevolvendo(null)}
          aoConcluir={(atualizada) => {
            setDevolvendo(null)
            setVersao((n) => n + 1)
            mostrarAviso(`Condicional ${atualizada.codigo} devolvida.`)
          }}
        />
      )}

      {cancelando && (
        <DialogoCancelarCondicional
          condicional={cancelando}
          aoFechar={() => setCancelando(null)}
          aoConcluir={(atualizada) => {
            setCancelando(null)
            setVersao((n) => n + 1)
            mostrarAviso(`Condicional ${atualizada.codigo} cancelada.`)
          }}
        />
      )}
    </LayoutApp>
  )
}

/* --------------------------------------------------------------- tabela */

function LinhaDaTabela({
  linha,
  ultima,
  aoAbrir,
  aoFaturar,
  aoDevolver,
  aoCancelar,
}: {
  linha: LinhaCondicional
  ultima: boolean
  aoAbrir: () => void
  aoFaturar: () => void
  aoDevolver: () => void
  aoCancelar: () => void
}) {
  const borda = ultima ? '' : 'border-b border-divisoria'
  const aberta = linha.status === 'aberta'

  return (
    <tr>
      <td className={`tabular py-2.5 pr-3 pl-4 text-[13px] font-semibold text-texto-suave ${borda}`}>
        {linha.codigo}
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <button
          type="button"
          onClick={aoAbrir}
          className="text-left text-[13.5px] font-semibold text-tinta hover:text-marca"
        >
          {linha.cliente.nome}
        </button>
        <div className="tabular mt-0.5 text-xs text-texto-suave">{linha.cliente.codigo}</div>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <div className="tabular text-[13px] text-tinta">
          {linha.pecas} {linha.pecas === 1 ? 'peça' : 'peças'}
        </div>
        <div className="text-xs text-texto-suave">
          {linha.itens} {linha.itens === 1 ? 'item' : 'itens'}
        </div>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <span className="tabular text-[13.5px] font-semibold text-tinta">
          {formatarMoeda(linha.subtotal_centavos)}
        </span>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <span className="tabular text-[13px] text-texto-suave">
          {linha.prazo ? dataISOparaBR(linha.prazo) : '—'}
        </span>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <SeloStatus linha={linha} />
      </td>

      <td className={`px-3 py-2.5 text-right ${borda}`}>
        {aberta ? (
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={aoFaturar}
              className="rounded-sistema border border-marca bg-marca-suave px-3 py-1.5 text-[12.5px] font-semibold text-marca-forte hover:bg-marca-barra"
            >
              Faturar
            </button>
            <button
              type="button"
              onClick={aoDevolver}
              className="rounded-sistema border border-borda px-3 py-1.5 text-[12.5px] font-semibold text-texto hover:bg-divisoria"
            >
              Devolver
            </button>
            <button
              type="button"
              onClick={aoCancelar}
              className="rounded-sistema border border-borda px-3 py-1.5 text-[12.5px] font-semibold text-texto-suave hover:border-erro hover:text-erro"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={aoAbrir}
            className="text-[12.5px] font-semibold text-marca hover:text-marca-forte"
          >
            Ver ficha
          </button>
        )}
      </td>
    </tr>
  )
}

function SeloStatus({ linha }: { linha: LinhaCondicional }) {
  const CORES: Record<string, string> = {
    aberta: 'border-marca-barra bg-marca-fundo-2 text-marca-forte',
    faturada: 'border-ok-suave bg-ok-suave text-ok-texto',
    devolvida: 'border-borda bg-superficie-suave text-texto-suave',
    cancelada: 'border-erro-suave bg-erro-suave text-erro-texto',
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-[6px] border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
        CORES[linha.status] ?? 'border-borda bg-superficie text-texto-suave'
      }`}
    >
      <Icone nome={ICONE_STATUS[linha.status] ?? 'atraso'} tamanho={13} />
      {linha.status_rotulo}
    </span>
  )
}

/** Skeleton de linha, nunca spinner de página inteira (spec 03). */
function Esqueleto() {
  return (
    <div className="flex flex-col gap-2 p-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-11 animate-pulse rounded-sistema bg-superficie-suave" />
      ))}
    </div>
  )
}

function EstadoVazio({
  temFiltro,
  podeCriar,
}: {
  temFiltro: boolean
  podeCriar: boolean
}) {
  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-14 text-center">
      <Icone nome={temFiltro ? 'search' : 'apparel'} tamanho={26} className="text-inativo" />
      <p className="text-sm font-semibold text-tinta">
        {temFiltro ? 'Nenhuma condicional encontrada' : 'Nenhuma condicional aberta ainda'}
      </p>
      <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">
        {temFiltro
          ? 'Ajuste a busca ou o filtro de situação.'
          : 'Abra uma condicional quando o cliente quiser levar a peça para decidir em casa.'}
      </p>
      {!temFiltro && podeCriar && (
        <div className="mt-2">
          <LinkBotao para="/app/condicionais/novo" icone="add">
            Abrir a primeira condicional
          </LinkBotao>
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- drawer */

function Drawer({ condicional, aoFechar }: { condicional: Condicional; aoFechar: () => void }) {
  return (
    <Gaveta rotulo={`Ficha de ${condicional.codigo}`} largura="tabela" aoFechar={aoFechar}>
      <CabecalhoDaGaveta
        titulo={`Condicional ${condicional.codigo}`}
        subtitulo={condicional.cliente.nome}
        rotuloFechar="Fechar ficha"
        aoFechar={aoFechar}
      >
        <span
          className={`shrink-0 rounded-[6px] border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
            condicional.status === 'aberta'
              ? 'border-marca-barra bg-marca-fundo-2 text-marca-forte'
              : condicional.status === 'faturada'
                ? 'border-ok-suave bg-ok-suave text-ok-texto'
                : condicional.status === 'cancelada'
                  ? 'border-erro-suave bg-erro-suave text-erro-texto'
                  : 'border-borda bg-superficie-suave text-texto-suave'
          }`}
        >
          {condicional.status_rotulo}
        </span>
      </CabecalhoDaGaveta>

      <CorpoDaGaveta>
        <Secao titulo="Itens">
          <div className="rounded-sistema border border-borda">
            {condicional.itens.map((item, indice) => (
              <div
                key={item.numero_item}
                className={`flex items-center gap-3 px-3.5 py-2.5 ${indice ? 'border-t border-divisoria' : ''}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-tinta">{item.sku.produto}</p>
                  <p className="tabular text-xs text-texto-suave">
                    {item.sku.cor} · {item.sku.tamanho} · {item.quantidade}×{' '}
                    {formatarMoeda(item.preco_unitario_centavos)}
                  </p>
                </div>
                <span className="tabular shrink-0 text-[13px] font-semibold text-tinta">
                  {formatarMoeda(item.total_centavos)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-2.5 flex justify-between text-[13.5px]">
            <span className="font-semibold text-tinta">Subtotal</span>
            <span className="tabular font-bold text-tinta">
              {formatarMoeda(condicional.subtotal_centavos)}
            </span>
          </div>
        </Secao>

        <Secao titulo="Condicional">
          <LinhaFicha rotulo="Aberta em" valor={dataISOparaBR(condicional.criado_em.slice(0, 10))} />
          {condicional.prazo && <LinhaFicha rotulo="Prazo" valor={dataISOparaBR(condicional.prazo)} />}
          {condicional.vendedor && <LinhaFicha rotulo="Vendedor" valor={condicional.vendedor} />}
          {condicional.observacao && <LinhaFicha rotulo="Observação" valor={condicional.observacao} />}
        </Secao>

        {condicional.status === 'faturada' && condicional.venda_codigo && (
          <Secao titulo="Faturamento">
            <LinhaFicha rotulo="Venda" valor={condicional.venda_codigo} />
            {condicional.faturada_em && (
              <LinhaFicha rotulo="Faturada em" valor={dataISOparaBR(condicional.faturada_em.slice(0, 10))} />
            )}
          </Secao>
        )}

        {condicional.status === 'devolvida' && (
          <Secao titulo="Devolução">
            {condicional.devolvida_em && (
              <LinhaFicha rotulo="Devolvida em" valor={dataISOparaBR(condicional.devolvida_em.slice(0, 10))} />
            )}
            <LinhaFicha rotulo="Motivo" valor={condicional.motivo_devolucao || 'Sem motivo registrado'} />
          </Secao>
        )}

        {condicional.status === 'cancelada' && (
          <Secao titulo="Cancelamento">
            {condicional.cancelada_em && (
              <LinhaFicha rotulo="Cancelada em" valor={dataISOparaBR(condicional.cancelada_em.slice(0, 10))} />
            )}
            <LinhaFicha rotulo="Motivo" valor={condicional.motivo_cancelamento ?? ''} />
          </Secao>
        )}
      </CorpoDaGaveta>

      {condicional.status === 'aberta' && (
        <RodapeDaGavetaAcoes codigo={condicional.codigo} />
      )}
    </Gaveta>
  )
}

/** Atalho para faturar direto da ficha — leva para a rota de faturamento,
 *  mesma que a listagem usa. */
function RodapeDaGavetaAcoes({ codigo }: { codigo: string }) {
  return (
    <footer className="flex min-h-[64px] shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-borda bg-superficie-suave px-5 py-3">
      <Link
        to={`/app/condicionais/${encodeURIComponent(codigo)}/faturar`}
        className="flex h-[34px] items-center justify-center gap-2 rounded-sistema bg-tinta px-4 text-[13px] font-bold text-white hover:bg-titulo"
      >
        <Icone nome="check_circle" tamanho={17} />
        Faturar
      </Link>
    </footer>
  )
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="mb-3 text-[11px] font-bold tracking-[0.6px] text-texto-fraco uppercase">
        {titulo}
      </h3>
      {children}
    </section>
  )
}

function LinhaFicha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-4 border-t border-divisoria py-2.5 text-[13.5px] first:border-t-0">
      <span className="text-texto-suave">{rotulo}</span>
      <span className="tabular min-w-0 text-right font-semibold break-words text-tinta">
        {valor}
      </span>
    </div>
  )
}
